import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/context/AuthContext";
import { useCampaign } from "@/context/CampaignContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Mail, Plus, PlusCircle, Search } from "lucide-react";
import { CampaignProvider } from "@/context/CampaignContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserCampaigns } from "../Redux/ReduxSlices/campaignSlice";

// Mock campaign data with better stats for visualization

const formatDate = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Simple bar chart component

// Campaign details modal component

function CampaignsPage() {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTimeframe, setSelectedTimeframe] = useState("last-30");

  const { campaigns } = useSelector((state) => state.campaign);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(fetchUserCampaigns());
  }, [dispatch]);

  const navigate = useNavigate();

  // Filter campaigns based on tab and search
  const filteredCampaigns = campaigns?.filter((campaign) => {
    const matchesTab =
      activeTab === "all" ||
      activeTab === campaign.status ||
      (activeTab === "active" &&
        (campaign.status === "active" || campaign.status === "sending")) ||
      (activeTab === "completed" && campaign.status === "completed") ||
      (activeTab === "failed" && campaign.status === "failed");

    const matchesSearch = campaign.campaign_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  // Handle campaign click
  const handleCampaignClick = (campaign) => {
    navigate(`/campaign/${campaign.id}`);
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/campaigns");
    }
  }, [user, navigate]);

  if (!user) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 hover:bg-green-200 ";
      case "completed":
        return "bg-blue-100 text-blue-800 hover:bg-blue-200 ";
      case "paused":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 ";
      case "draft":
        return "bg-gray-100 text-gray-800 hover:bg-gray-200 ";
      case "replied":
        return "bg-green-100 text-green-800 hover:bg-green-200 ";
      case "clicked":
        return "bg-blue-100 text-blue-800 hover:bg-blue-200 ";
      case "opened":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 ";
      case "delivered":
        return "bg-gray-100 text-gray-800 hover:bg-gray-200 ";
      case "failed":
        return "bg-red-300 text-red-800 hover:bg-red-400 ";
      case "queued":
        return "bg-yellow-500 text-black hover:bg-yellow-600 ";
      case "sending":
        return "bg-blue-600 text-white hover:bg-blue-600 text-sm";
      default:
        return "bg-gray-300 text-gray-900 hover:bg-gray-200 ";
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-10 max-md:px-6 py-6 max-md:py-4">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h4 className="">Email Campaigns</h4>
            <p className="text-black  max-md:text-sm mb-1 ">
              Manage and monitor your outreach campaigns
            </p>
          </div>

          <div className="flex flex-row gap-2 max-md:w-full justify-between">
            {/* Dashboard */}
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="border border-indigo-700 w-1/2 md:w-auto justify-center"
            >
              <span className="hidden md:inline">View Dashboard</span>
              <span className="inline md:hidden">Dashboard</span>
            </Button>

            {/* Show New Campaign ONLY if campaigns exist */}
            {campaigns?.length > 0 && (
              <Button
                onClick={() => navigate("/search")}
                className="main-button w-1/2 md:w-auto justify-center"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                New Campaign
              </Button>
            )}
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="col-span-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search campaigns..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Campaign Tabs and List */}
        <Card>
          <CardHeader className="px-4 py-3">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList
                className="
      flex sm:grid sm:grid-cols-4
      w-full
      bg-white/80 backdrop-blur-sm
      rounded-md shadow-lg border border-black/20
      overflow-x-auto sm:overflow-visible
      no-scrollbar
    "
              >
                <TabsTrigger
                  value="all"
                  className="
        flex-1 sm:flex-none
        whitespace-nowrap
        text-xs sm:text-sm
        px-3 sm:px-4 py-2
        font-bold
        data-[state=active]:bg-gradient-to-r
        data-[state=active]:from-blue-600
        data-[state=active]:to-indigo-600
        data-[state=active]:text-white
      "
                >
                  All
                </TabsTrigger>

                <TabsTrigger
                  value="active"
                  className="flex-1 sm:flex-none whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 py-2 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white"
                >
                  Active
                </TabsTrigger>

                <TabsTrigger
                  value="completed"
                  className="flex-1 sm:flex-none whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 py-2 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white"
                >
                  Completed
                </TabsTrigger>

                <TabsTrigger
                  value="failed"
                  className="flex-1 sm:flex-none whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 py-2 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white"
                >
                  Failed
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>

          <CardContent className="p-0">
            {filteredCampaigns?.length > 0 ? (
              <div className="divide-y">
                {filteredCampaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex justify-between items-center p-4 hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleCampaignClick(campaign)}
                  >
                    <div className="space-y-1">
                      <div className="relative group cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Mail
                            className={`h-5 w-5 ${
                              campaign.status === "active"
                                ? "text-green-500"
                                : campaign.status === "completed"
                                  ? "text-blue-500"
                                  : campaign.status === "queued"
                                    ? "text-amber-500"
                                    : campaign.status === "failed"
                                      ? "text-red-500"
                                      : campaign.status === "paused"
                                        ? "text-yellow-600"
                                        : campaign.status === "sending"
                                          ? "text-blue-600"
                                          : "text-white-400"
                            }`}
                          />

                          <span className="font-medium">
                            {campaign?.campaign_name?.trim()
                              ? campaign.campaign_name.charAt(0).toUpperCase() +
                                campaign.campaign_name.slice(1)
                              : "--"}
                          </span>

                          <Badge className={getStatusColor(campaign.status)}>
                            {campaign.status === "sending"
                              ? "In Progress"
                              : campaign.status.charAt(0).toUpperCase() +
                                campaign.status.slice(1)}
                          </Badge>
                        </div>

                        {/* Tooltip */}
                        <div
                          className="absolute left-1/2 -translate-x-1/2 top-full mt-8 
               whitespace-nowrap rounded-md bg-gray-900 px-3 py-1.5 
               text-xs text-white opacity-0 scale-95 
               group-hover:opacity-100 group-hover:scale-100 
               transition-all duration-200 z-50"
                        >
                          Click here to see campaign details
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground">
                        {campaign.created_at
                          ? `Sent on ${formatDate(campaign.created_at + "Z")}`
                          : "Not sent yet"}{" "}
                        • {campaign.total_recipients} recipients
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Mail className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-1">No campaigns found</h3>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  {searchQuery
                    ? "No campaigns match your search criteria."
                    : "You don't have any campaigns yet. Please click on 'New Campaign' button to start your campaign now"}
                </p>
                {/* <Button
                  onClick={() => navigate("/search")}
                  className="main-button text-sm font-medium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  {" "}
                  <PlusCircle size={18} className="mr-2" />
                  Create Your First Campaign
                </Button> */}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}

// Wrapper with provider
export default function CampaignsPageWrapper() {
  return (
    <CampaignProvider>
      <CampaignsPage />
    </CampaignProvider>
  );
}
